import { OrderPredicateAPI } from "@dashboard/discounts/types";

import { ORDER_ITEM_CONDITIONS } from "./constants";

const PRICE_CONDITIONS = ["baseSubtotalPrice", "baseTotalPrice"];
const PRICE_FILTER_KEYS = ["eq", "range"];

type OrderPredicate = OrderPredicateAPI["discountedObjectPredicate"];

// The rule form can only show the shapes prepareOrderPredicate writes:
// {}, a single condition field, or an OR list of single condition fields.
// Anything else (AND, nested OR, several fields in one object) would be
// rewritten on save, so it is treated as too complex to edit.
export function hasOrderPredicateComplexConditions(
  predicate: OrderPredicate | null | undefined,
): boolean {
  const entries = definedEntries(predicate);

  if (entries.length === 0) {
    return false;
  }

  const [key, value] = entries[0];

  if (entries.length === 1 && key === "OR") {
    return !Array.isArray(value) || !value.every(isSingleCondition);
  }

  return !isSingleCondition(predicate);
}

function isSingleCondition(predicate: unknown): boolean {
  const entries = definedEntries(predicate);

  if (entries.length !== 1) {
    return false;
  }

  const [id, filter] = entries[0];

  return isEditableFilter(id, filter);
}

function isEditableFilter(id: string, filter: unknown): boolean {
  const keys = definedEntries(filter).map(([key]) => key);

  if (keys.length !== 1) {
    return false;
  }

  if (ORDER_ITEM_CONDITIONS.includes(id)) {
    return (
      keys[0] === "oneOf" && Array.isArray((filter as { oneOf: unknown }).oneOf)
    );
  }

  if (PRICE_CONDITIONS.includes(id)) {
    return PRICE_FILTER_KEYS.includes(keys[0]);
  }

  return false;
}

function definedEntries(value: unknown): Array<[string, unknown]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value).filter(
    ([, entryValue]) => entryValue !== null && entryValue !== undefined,
  );
}
